import { parsePartialJsonObject } from 'assistant-stream/utils'

import {
  AbstractChatSession,
  ChatMessage,
  ModelRequestPart,
  ModelResponsePart,
  ModelResponsePartDelta,
  MsgDelta,
  NativeToolCallPart,
  NativeToolSearchCallPart,
  RunIdToMsgIndex,
  ToolCallPart,
  ToolCallPartDelta,
  ToolSearchArgs,
  ToolSearchCallPart,
} from '@/types/chat-v2'

export interface MessageDeltaState {
  buffers: Record<RunIdToMsgIndex, Map<number, MsgDelta>>
  messageIndexes: Record<RunIdToMsgIndex, number>
  allMessagesIndexes: Record<RunIdToMsgIndex, number>
  nextExpectedSequence: Record<RunIdToMsgIndex, number>
}

export function createMessageDeltaState(): MessageDeltaState {
  return {
    buffers: {},
    messageIndexes: {},
    allMessagesIndexes: {},
    nextExpectedSequence: {},
  }
}

export function clearMessageDeltaState(state: MessageDeltaState): void {
  state.buffers = {}
  state.messageIndexes = {}
  state.allMessagesIndexes = {}
  state.nextExpectedSequence = {}
}

export function seedMessageDeltaStateFromSession<
  SessionT extends AbstractChatSession,
>(state: MessageDeltaState, session: SessionT): void {
  session.all_messages.forEach((message, index) => {
    state.messageIndexes[message.id] = message.index_in_thread
    state.allMessagesIndexes[message.id] = index
    if (message.last_delta_sequence != null) {
      state.nextExpectedSequence[message.id] = message.last_delta_sequence + 1
    }
  })
}

function cloneRequestPart(
  part: ModelRequestPart,
  partIndex: number,
  parts: ModelRequestPart[],
): ModelRequestPart[] {
  const next = [...parts]
  next[partIndex] = part
  return next
}

function cloneResponsePart(
  part: ModelResponsePart,
  partIndex: number,
  parts: ModelResponsePart[],
): ModelResponsePart[] {
  const next = [...parts]
  next[partIndex] = part
  return next
}

const MODEL_REQUEST_PART_KINDS = {
  'system-prompt': true,
  'user-prompt': true,
  'tool-return': true,
  'retry-prompt': true,
} as const satisfies Record<ModelRequestPart['part_kind'], true>

const MODEL_RESPONSE_PART_KINDS = {
  text: true,
  thinking: true,
  'tool-call': true,
  'builtin-tool-call': true,
  'builtin-tool-return': true,
  compaction: true,
  file: true,
} as const satisfies Record<ModelResponsePart['part_kind'], true>

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function isModelRequestPart(part: unknown): part is ModelRequestPart {
  return (
    isRecord(part) &&
    typeof part.part_kind === 'string' &&
    part.part_kind in MODEL_REQUEST_PART_KINDS
  )
}

export function isModelResponsePart(part: unknown): part is ModelResponsePart {
  return (
    isRecord(part) &&
    typeof part.part_kind === 'string' &&
    part.part_kind in MODEL_RESPONSE_PART_KINDS
  )
}

export function isClosedResponseMessage(
  message: ChatMessage | undefined,
): boolean {
  if (message?.message.kind !== 'response') return false
  return (
    message.message.state === 'complete' ||
    message.message.state === 'interrupted' ||
    message.message.finish_reason != null
  )
}

function isToolSearchArgs(value: unknown): value is ToolSearchArgs {
  return (
    isRecord(value) &&
    Array.isArray(value.queries) &&
    value.queries.every((query) => typeof query === 'string')
  )
}

function mergeToolCallArgs(
  args: string | Record<string, unknown> | null,
  argsDelta: string | Record<string, unknown> | null | undefined,
): string | Record<string, unknown> | null {
  if (typeof argsDelta === 'string') {
    return typeof args === 'string' ? args + argsDelta : argsDelta
  }
  if (!argsDelta) {
    return args
  }
  if (typeof args === 'string') {
    return {
      ...(parsePartialJsonObject(args) ?? {}),
      ...argsDelta,
    }
  }
  return {
    ...(args ?? {}),
    ...argsDelta,
  }
}

function mergeToolSearchArgs(
  args: string | ToolSearchArgs | null,
  argsDelta: string | Record<string, unknown> | null | undefined,
): string | ToolSearchArgs | null {
  if (typeof argsDelta === 'string') {
    return typeof args === 'string' ? args + argsDelta : argsDelta
  }
  if (!argsDelta) {
    return args
  }

  const existingArgs =
    typeof args === 'string' ? parsePartialJsonObject(args) : args
  const mergedArgs = {
    ...(isRecord(existingArgs) ? existingArgs : {}),
    ...argsDelta,
  }
  return isToolSearchArgs(mergedArgs) ? mergedArgs : args
}

function mergeProviderDetails(
  existing: Record<string, unknown> | null | undefined,
  next: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null | undefined {
  if (existing == null && next == null) {
    return existing === undefined && next === undefined ? undefined : null
  }

  const merged = { ...(existing ?? {}), ...(next ?? {}) }
  return Object.keys(merged).length > 0 ? merged : null
}

function withProviderFields<T extends object>(
  base: T,
  providerName: string | null | undefined,
  providerDetails: Record<string, unknown> | null | undefined,
): T & {
  provider_name?: string | null
  provider_details?: Record<string, unknown> | null
} {
  return {
    ...base,
    ...(providerName !== undefined ? { provider_name: providerName } : {}),
    ...(providerDetails !== undefined
      ? { provider_details: providerDetails }
      : {}),
  }
}

function isToolSearchCallPart(
  part: ModelResponsePart,
): part is ToolSearchCallPart {
  return (
    part.part_kind === 'tool-call' &&
    part.tool_kind === 'tool-search' &&
    part.tool_name === 'search_tools'
  )
}

function isNativeToolSearchCallPart(
  part: ModelResponsePart,
): part is NativeToolSearchCallPart {
  return (
    part.part_kind === 'builtin-tool-call' &&
    part.tool_kind === 'tool-search' &&
    part.tool_name === 'tool_search'
  )
}

function applyToolCallDelta(
  part: ToolCallPart | NativeToolCallPart,
  delta: ToolCallPartDelta,
): ToolCallPart | NativeToolCallPart {
  return withProviderFields(
    {
      ...part,
      tool_name: part.tool_name + (delta.tool_name_delta ?? ''),
      tool_call_id: delta.tool_call_id ?? part.tool_call_id,
      args: mergeToolCallArgs(part.args, delta.args_delta),
    },
    delta.provider_name || part.provider_name,
    mergeProviderDetails(part.provider_details, delta.provider_details),
  )
}

function applyToolSearchCallDelta(
  part: ToolSearchCallPart,
  delta: ToolCallPartDelta,
): ToolSearchCallPart {
  return withProviderFields(
    {
      ...part,
      tool_name: 'search_tools',
      tool_call_id: delta.tool_call_id ?? part.tool_call_id,
      args: mergeToolSearchArgs(part.args, delta.args_delta),
    },
    delta.provider_name || part.provider_name,
    mergeProviderDetails(part.provider_details, delta.provider_details),
  )
}

function applyNativeToolSearchCallDelta(
  part: NativeToolSearchCallPart,
  delta: ToolCallPartDelta,
): NativeToolSearchCallPart {
  return withProviderFields(
    {
      ...part,
      tool_name: 'tool_search',
      tool_call_id: delta.tool_call_id ?? part.tool_call_id,
      args: mergeToolSearchArgs(part.args, delta.args_delta),
    },
    delta.provider_name || part.provider_name,
    mergeProviderDetails(part.provider_details, delta.provider_details),
  )
}

export function applyResponsePartDelta(
  part: ModelResponsePart | undefined,
  delta: ModelResponsePartDelta,
): ModelResponsePart | undefined {
  if (!part) {
    if (delta.part_delta_kind === 'tool_call' && delta.tool_name_delta) {
      return withProviderFields(
        {
          part_kind: 'tool-call' as const,
          tool_name: delta.tool_name_delta,
          tool_call_id: delta.tool_call_id ?? '',
          args: delta.args_delta ?? '',
        },
        delta.provider_name ?? undefined,
        delta.provider_details ?? undefined,
      )
    }
    return undefined
  }

  switch (delta.part_delta_kind) {
    case 'text':
      if (part.part_kind !== 'text') return part
      return withProviderFields(
        {
          ...part,
          content: part.content + delta.content_delta,
        },
        delta.provider_name || part.provider_name,
        mergeProviderDetails(part.provider_details, delta.provider_details),
      )

    case 'thinking':
      if (part.part_kind !== 'thinking') return part
      return withProviderFields(
        {
          ...part,
          content: part.content + (delta.content_delta ?? ''),
          signature: delta.signature_delta ?? part.signature,
        },
        delta.provider_name ?? part.provider_name,
        mergeProviderDetails(part.provider_details, delta.provider_details),
      )

    case 'tool_call': {
      if (isToolSearchCallPart(part)) {
        return applyToolSearchCallDelta(part, delta)
      }
      if (isNativeToolSearchCallPart(part)) {
        return applyNativeToolSearchCallDelta(part, delta)
      }
      if (
        part.part_kind === 'tool-call' ||
        part.part_kind === 'builtin-tool-call'
      ) {
        return applyToolCallDelta(part, delta)
      }
      return part
    }
  }
}

function upsertAllMessages(
  allMessages: ChatMessage[],
  message: ChatMessage,
  state: MessageDeltaState,
): ChatMessage[] {
  const next = [...allMessages]
  const existingIndex = state.allMessagesIndexes[message.id]
  if (existingIndex === undefined) {
    state.allMessagesIndexes[message.id] = next.length
    next.push(message)
  } else {
    next[existingIndex] = message
  }
  return next
}

function resolveMessageIndex<SessionT extends AbstractChatSession>(
  session: SessionT,
  event: MsgDelta,
  state: MessageDeltaState,
): number | undefined {
  switch (event.delta.op) {
    case 'init_request':
    case 'init_response':
      state.messageIndexes[event.message_id] = event.delta.index_in_thread
      return event.delta.index_in_thread

    case 'set_part':
    case 'apply_part_delta':
    case 'set_deferred_tool_calls': {
      const knownIndex = state.messageIndexes[event.message_id]
      if (knownIndex !== undefined) return knownIndex

      const existingAllMessagesIndex = session.all_messages.findIndex(
        (message) => message.id === event.message_id,
      )
      const existingMessage = session.all_messages[existingAllMessagesIndex]
      if (!existingMessage) return undefined

      state.messageIndexes[event.message_id] = existingMessage.index_in_thread
      state.allMessagesIndexes[event.message_id] = existingAllMessagesIndex
      return existingMessage.index_in_thread
    }
  }
}

export function applyMessageDeltaToSession<
  SessionT extends AbstractChatSession,
>(session: SessionT, event: MsgDelta, state: MessageDeltaState): SessionT {
  const messageIndex = resolveMessageIndex(session, event, state)
  if (messageIndex === undefined) {
    console.warn('[message-delta] dropping delta for unknown message', {
      messageId: event.message_id,
      sequence: event.sequence,
      operation: event.delta.op,
    })
    return session
  }

  const thread = [...session.current_thread]
  const allMessages = session.all_messages

  switch (event.delta.op) {
    case 'init_request': {
      const message: ChatMessage = {
        id: event.message_id,
        parent_id: event.delta.parent_id,
        run_id: event.delta.run_id,
        run_parent_id: event.delta.run_parent_id,
        run_user_id: event.delta.run_user_id,
        index_in_thread: event.delta.index_in_thread,
        message: {
          kind: 'request',
          parts: [],
        },
      }

      thread[messageIndex] = message
      return {
        ...session,
        current_thread: thread,
        all_messages: upsertAllMessages(allMessages, message, state),
        current_run_id: message.run_id,
      }
    }

    case 'init_response': {
      const message: ChatMessage = {
        id: event.message_id,
        parent_id: event.delta.parent_id,
        run_id: event.delta.run_id,
        run_parent_id: event.delta.run_parent_id,
        run_user_id: event.delta.run_user_id,
        index_in_thread: event.delta.index_in_thread,
        message: {
          kind: 'response',
          parts: [],
          model_name: event.delta.model_name,
          timestamp: event.delta.timestamp,
        },
      }

      thread[messageIndex] = message
      return {
        ...session,
        current_thread: thread,
        all_messages: upsertAllMessages(allMessages, message, state),
        current_run_id: message.run_id,
      }
    }

    case 'set_part': {
      const message = thread[messageIndex]
      if (!message) return session

      let updatedMessage: ChatMessage
      if (message.message.kind === 'request') {
        if (!isModelRequestPart(event.delta.part)) return session
        updatedMessage = {
          ...message,
          message: {
            ...message.message,
            parts: cloneRequestPart(
              event.delta.part,
              event.delta.part_index,
              message.message.parts,
            ),
          },
        }
      } else {
        if (isClosedResponseMessage(message)) return session
        if (!isModelResponsePart(event.delta.part)) return session
        updatedMessage = {
          ...message,
          message: {
            ...message.message,
            parts: cloneResponsePart(
              event.delta.part,
              event.delta.part_index,
              message.message.parts,
            ),
          },
        }
      }

      thread[messageIndex] = updatedMessage
      return {
        ...session,
        current_thread: thread,
        all_messages: upsertAllMessages(allMessages, updatedMessage, state),
      }
    }

    case 'apply_part_delta': {
      const message = thread[messageIndex]
      if (!message || message.message.kind !== 'response') return session
      if (isClosedResponseMessage(message)) return session

      const updatedPart = applyResponsePartDelta(
        message.message.parts[event.delta.part_index],
        event.delta.delta,
      )
      if (!updatedPart) return session

      const updatedMessage: ChatMessage = {
        ...message,
        message: {
          ...message.message,
          parts: cloneResponsePart(
            updatedPart,
            event.delta.part_index,
            message.message.parts,
          ),
        },
      }

      thread[messageIndex] = updatedMessage
      return {
        ...session,
        current_thread: thread,
        all_messages: upsertAllMessages(allMessages, updatedMessage, state),
      }
    }

    case 'set_deferred_tool_calls': {
      const message = thread[messageIndex]
      if (!message) return session

      const updatedMessage: ChatMessage = {
        ...message,
        deferred_tool_calls: event.delta.deferred_tool_calls,
      }

      thread[messageIndex] = updatedMessage
      return {
        ...session,
        current_thread: thread,
        all_messages: upsertAllMessages(allMessages, updatedMessage, state),
      }
    }
  }
}

export function applyBufferedMessageDeltaToSession<
  SessionT extends AbstractChatSession,
>(session: SessionT, event: MsgDelta, state: MessageDeltaState): SessionT {
  const knownNextExpected = state.nextExpectedSequence[event.message_id]
  if (knownNextExpected !== undefined && event.sequence < knownNextExpected) {
    return session
  }

  const buffer = state.buffers[event.message_id] ?? new Map<number, MsgDelta>()
  buffer.set(event.sequence, event)
  state.buffers[event.message_id] = buffer

  let nextSession = session
  let nextExpected = knownNextExpected ?? event.sequence

  for (const sequence of [...buffer.keys()]) {
    if (sequence < nextExpected) {
      buffer.delete(sequence)
    }
  }

  while (buffer.has(nextExpected)) {
    const bufferedEvent = buffer.get(nextExpected)
    if (!bufferedEvent) break
    buffer.delete(nextExpected)
    nextSession = applyMessageDeltaToSession(nextSession, bufferedEvent, state)
    nextExpected += 1
  }

  state.nextExpectedSequence[event.message_id] = nextExpected
  if (buffer.size === 0) {
    delete state.buffers[event.message_id]
  }

  return nextSession
}
